/**
 * Notification i18n helpers shared across:
 * - full page notifications list
 * - realtime toasts
 * - compact notification center dropdown
 *
 * Why this exists:
 * - Novu notification objects can vary in shape depending on source (hook vs realtime event).
 * - We want a consistent, language-aware title/body rendering strategy.
 * - New notifications may ship explicit `i18n` metadata; legacy ones can be derived best-effort.
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeLanguage(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function normalizeComparableText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/Ä…/g, 'a')
    .replace(/Ä™/g, 'e')
    .replace(/Ăł/g, 'o')
    .replace(/Ĺ›/g, 's')
    .replace(/Ĺ„/g, 'n')
    .replace(/Ĺ¼/g, 'z')
    .replace(/Ĺş/g, 'z')
    .replace(/Ĺ‚/g, 'l')
    .replace(/ą/g, 'a')
    .replace(/ć/g, 'c')
    .replace(/ę/g, 'e')
    .replace(/ł/g, 'l')
    .replace(/ń/g, 'n')
    .replace(/ó/g, 'o')
    .replace(/ś/g, 's')
    .replace(/ż/g, 'z')
    .replace(/ź/g, 'z')
    .replace(/\s+/g, ' ');
}

/**
 * Convert ISO yyyy-mm-dd dates to a display format matching the UI language.
 * For now, we only special-case Polish to use dd.mm.yyyy.
 *
 * @param {unknown} dateValue
 * @param {unknown} language
 * @returns {string}
 */
export function formatIsoDateForLanguage(dateValue, language) {
  const iso = String(dateValue || '');
  const lang = normalizeLanguage(language);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;

  if (lang === 'pl' || lang.startsWith('pl-')) {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  }

  return iso;
}

/**
 * @param {any} payload
 * @returns {string}
 */
function resolveNotificationTimezone(payload) {
  return (
    payload?.timezone ||
    payload?.userTimezone ||
    payload?.teacherTimezone ||
    payload?.studentTimezone ||
    (() => {
      if (typeof window !== 'undefined') {
        try {
          const preferred = localStorage.getItem('selectedTimezone');
          if (preferred) return String(preferred);
        } catch (_) {
          // ignore
        }
      }
      return null;
    })() ||
    (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null) ||
    'UTC'
  );
}

/**
 * @param {Date} date
 * @param {string} timezone
 * @returns {string}
 */
function formatIsoDateInTimezone(date, timezone) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch (_) {
    // ignore
  }
  return '';
}

/**
 * @param {Date} date
 * @param {string} timezone
 * @returns {string}
 */
function formatTimeInTimezone(date, timezone) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const hour = parts.find((p) => p.type === 'hour')?.value;
    const minute = parts.find((p) => p.type === 'minute')?.value;
    if (hour && minute) return `${hour}:${minute}`;
  } catch (_) {
    // ignore
  }
  return '';
}

/**
 * Build i18n interpolation params from payload fields as a fallback.
 *
 * @param {any} payload
 * @param {string} language
 * @returns {Record<string, any>}
 */
function deriveI18nParamsFromPayload(payload, language) {
  if (!payload || typeof payload !== 'object') return {};

  const out = {};
  const timezone = resolveNotificationTimezone(payload);
  const startDate = payload?.startTime ? new Date(payload.startTime) : null;
  const endDate = payload?.endTime ? new Date(payload.endTime) : null;
  const startValid = startDate instanceof Date && !Number.isNaN(startDate.getTime());
  const endValid = endDate instanceof Date && !Number.isNaN(endDate.getTime());

  const lessonDate = String(
    payload?.lessonDate ||
    payload?.bookingDate ||
    payload?.trialDate ||
    ''
  ).trim();
  const lessonTime = String(
    payload?.lessonTime ||
    payload?.bookingTime ||
    payload?.trialTime ||
    ''
  ).trim();
  const localStart = String(payload?.localStartTime || '').trim();
  const localEnd = String(payload?.localEndTime || '').trim();

  let dateIso = lessonDate;
  let start = localStart;
  let end = localEnd;

  if (!dateIso && startValid) {
    dateIso = formatIsoDateInTimezone(startDate, timezone);
  }
  if (!start && startValid) {
    start = formatTimeInTimezone(startDate, timezone);
  }
  if (!end && endValid) {
    end = formatTimeInTimezone(endDate, timezone);
  }

  const timeRange = lessonTime || (start && end ? `${start} - ${end}` : start || end || '');
  const counterpart =
    payload?.counterpart ||
    (String(payload?.userType || '').toLowerCase() === 'student'
      ? payload?.teacherName || payload?.teacherDisplayName || payload?.teacher
      : payload?.studentName || payload?.studentDisplayName || payload?.student) ||
    payload?.teacherName ||
    payload?.teacherDisplayName ||
    payload?.studentName ||
    payload?.studentDisplayName;

  if (dateIso) out.date = formatIsoDateForLanguage(dateIso, language);
  if (timeRange) out.time = timeRange;
  if (start) out.start = start;
  if (end) out.end = end;
  if (counterpart) out.counterpart = String(counterpart);
  if (payload?.teacherName || payload?.teacherDisplayName || payload?.teacher) {
    out.teacher = String(payload.teacherName || payload.teacherDisplayName || payload.teacher);
  }
  if (payload?.studentName || payload?.studentDisplayName || payload?.student) {
    out.student = String(payload.studentName || payload.studentDisplayName || payload.student);
  }
  if (payload?.timezone || timezone) out.timezone = String(payload?.timezone || timezone);
  if (payload?.packageName) out.packageName = String(payload.packageName);
  if (payload?.lessonTitle) out.lessonTitle = String(payload.lessonTitle);
  if (payload?.subject) out.subject = String(payload.subject);
  if (payload?.bookingCode) out.bookingCode = String(payload.bookingCode);
  if (payload?.hours != null) out.hours = String(payload.hours);
  if (payload?.amount != null) out.amount = String(payload.amount);
  if (payload?.currency != null) out.currency = String(payload.currency);
  if (payload?.period != null) out.period = String(payload.period);
  if (payload?.studentName || payload?.userName) {
    out.studentName = String(payload.studentName || payload.userName);
  }

  return out;
}

function hasMeaningfulText(value) {
  const text = String(value ?? '').trim();
  if (!text) return false;
  const lowered = text.toLowerCase();
  return lowered !== 'undefined' && lowered !== 'null' && text !== '[object Object]';
}

function joinNameParts(...parts) {
  return parts.map((part) => String(part ?? '').trim()).filter(hasMeaningfulText).join(' ').trim();
}

function resolvePayloadDisplayName(value) {
  if (!value) return '';
  if (typeof value !== 'object') {
    return hasMeaningfulText(value) ? String(value).trim() : '';
  }

  const fullName = joinNameParts(value.firstName, value.lastName);
  const candidates = [
    value.studentName,
    value.teacherName,
    value.userName,
    value.studentDisplayName,
    value.teacherDisplayName,
    value.displayName,
    value.fullName,
    value.name,
    value.Name,
    value.Username,
    value.username,
    fullName,
    value.Email,
    value.email,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object') continue;
    if (hasMeaningfulText(candidate)) return String(candidate).trim();
  }

  for (const nested of [value.user, value.student, value.teacher, value.studentData, value.teacherData, value.guestInfo]) {
    const resolved = resolvePayloadDisplayName(nested);
    if (resolved) return resolved;
  }

  return '';
}

function isPlaceholderValue(value, placeholders) {
  const normalized = normalizeComparableText(value);
  return !normalized || placeholders.includes(normalized);
}

function pickFirstConcreteDisplayName(placeholders, ...values) {
  for (const value of values) {
    const resolved = resolvePayloadDisplayName(value);
    if (resolved && !isPlaceholderValue(resolved, placeholders)) return resolved;
  }
  return '';
}

function objectCandidate(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : undefined;
}

function shouldSuppressPlaceholderAdminBody(payload, rawTitle, rawBody) {
  const text = normalizeComparableText(rawBody);
  if (!text) return false;

  if (isAdminBookingAlertPayload(payload, rawTitle)) {
    return text.includes('student') && text.includes('teacher') && (
      text.includes('tbd') ||
      text.includes(' on at ') ||
      text.includes(' w dniu o ')
    );
  }

  if (isAdminUserRegistrationAlertPayload(payload, rawTitle)) {
    return (
      text.includes('student') &&
      (
        text.includes('new user has registered') ||
        text.includes('zarejestrowal sie nowy uzytkownik')
      )
    );
  }

  return false;
}

function hasIncompletePlaceholderAdminI18n(payload, rawTitle, i18nMeta) {
  if (!i18nMeta || typeof i18nMeta !== 'object') return false;
  const params = i18nMeta.params && typeof i18nMeta.params === 'object' ? i18nMeta.params : {};

  if (isAdminBookingAlertPayload(payload, rawTitle)) {
    return (
      isPlaceholderValue(params.studentName || payload?.studentName, ['student']) ||
      isPlaceholderValue(params.teacherName || payload?.teacherName, ['teacher']) ||
      isPlaceholderValue(params.date || payload?.lessonDate || payload?.date, ['tbd']) ||
      isPlaceholderValue(params.time || payload?.lessonTime || payload?.time, ['tbd'])
    );
  }

  if (isAdminUserRegistrationAlertPayload(payload, rawTitle)) {
    return isPlaceholderValue(params.studentName || payload?.studentName || payload?.userName, ['student']);
  }

  return false;
}

function isAdminPaymentAlertPayload(payload, rawTitle = '') {
  if (!payload || typeof payload !== 'object') return false;

  const eventType = String(payload.eventType || '').trim().toUpperCase().replace(/-/g, '_');
  const actionUrl = String(payload.actionUrl || payload.url || '').trim();
  const rawTitleText = String(rawTitle || payload.title || '').trim().toLowerCase();
  const title = normalizeComparableText(rawTitle || payload.title || '');
  const tags = Array.isArray(payload.tags) ? payload.tags.map((tag) => String(tag).toLowerCase()) : [];

  return (
    eventType === 'ADMIN_ALERT_PAYMENT' ||
    actionUrl.includes('/Admin-Dashboard/payments') ||
    actionUrl.includes('/Admin-Dashboard/Payments') ||
    tags.includes('payment-alert') ||
    title.includes('admin payment alert') ||
    title.includes('powiadomienie o platnosci administracyjnej') ||
    (rawTitleText.includes('powiadomienie o') && rawTitleText.includes('administracyj'))
  );
}

export function deriveAdminAlertPaymentI18nFromPayload(payload, rawTitle = '') {
  if (!payload || typeof payload !== 'object') return null;
  if (!isAdminPaymentAlertPayload(payload, rawTitle)) return null;

  const studentName =
    payload.studentName ||
    payload.userName ||
    payload.username ||
    payload.Username ||
    [payload.firstName, payload.lastName].filter(Boolean).join(' ') ||
    payload.name;
  const amount =
    payload.amount ??
    payload.totalAmount ??
    payload.purchaseAmount ??
    payload.Purchase_Amount ??
    payload.value;
  const currency = payload.currency || payload.currencyCode || 'PLN';

  if (!studentName || amount == null || amount === '') return null;

  return {
    titleKey: 'notifications.events.adminAlertPayment.title',
    bodyKey: 'notifications.events.adminAlertPayment.body',
    params: {
      studentName,
      amount,
      currency,
    },
  };
}

function isAdminBookingAlertPayload(payload, rawTitle = '') {
  if (!payload || typeof payload !== 'object') return false;

  const eventType = String(payload.eventType || '').trim().toUpperCase().replace(/-/g, '_');
  const actionUrl = String(payload.actionUrl || payload.url || '').trim();
  const title = normalizeComparableText(rawTitle || payload.title || '');

  return (
    eventType === 'ADMIN_ALERT_BOOKING' ||
    actionUrl.includes('/Admin-Dashboard/Bookings') ||
    actionUrl.includes('/Admin-Dashboard/bookings') ||
    title.includes('admin booking alert') ||
    title.includes('powiadomienie o rezerwacji administratora')
  );
}

export function deriveAdminAlertBookingI18nFromPayload(payload, rawTitle = '') {
  if (!payload || typeof payload !== 'object') return null;
  if (!isAdminBookingAlertPayload(payload, rawTitle)) return null;

  const studentName = pickFirstConcreteDisplayName(
    ['student'],
    payload.studentName,
    payload.studentDisplayName,
    payload.userName,
    payload.student,
    payload.user,
    payload.studentData,
    objectCandidate(payload.studentId),
    payload.guestInfo
  );
  const teacherName = pickFirstConcreteDisplayName(
    ['teacher'],
    payload.teacherName,
    payload.teacherDisplayName,
    payload.teacher,
    payload.teacherData,
    objectCandidate(payload.teacherId)
  );
  const date =
    payload.lessonDate ||
    payload.bookingDate ||
    payload.localDate ||
    payload.date ||
    '';
  const time =
    payload.lessonTime ||
    payload.bookingTime ||
    payload.localTime ||
    payload.time ||
    '';

  if (
    isPlaceholderValue(studentName, ['student']) ||
    isPlaceholderValue(teacherName, ['teacher']) ||
    isPlaceholderValue(date, ['tbd']) ||
    isPlaceholderValue(time, ['tbd'])
  ) {
    return null;
  }

  return {
    titleKey: 'notifications.events.adminAlertBooking.title',
    bodyKey: 'notifications.events.adminAlertBooking.body',
    params: {
      studentName,
      teacherName,
      date,
      time,
    },
  };
}

function isAdminUserRegistrationAlertPayload(payload, rawTitle = '') {
  if (!payload || typeof payload !== 'object') return false;

  const eventType = String(payload.eventType || '').trim().toUpperCase().replace(/-/g, '_');
  const actionUrl = String(payload.actionUrl || payload.url || '').trim();
  const title = normalizeComparableText(rawTitle || payload.title || '');

  return (
    eventType === 'ADMIN_ALERT_USER_REGISTRATION' ||
    actionUrl.includes('/Admin-Dashboard/students') ||
    actionUrl.includes('/Admin-Dashboard/users') ||
    title.includes('new user registration') ||
    title.includes('rejestracja nowego')
  );
}

export function deriveAdminAlertUserRegistrationI18nFromPayload(payload, rawTitle = '') {
  if (!payload || typeof payload !== 'object') return null;
  if (!isAdminUserRegistrationAlertPayload(payload, rawTitle)) return null;

  const legacyFullName = [payload.firstName, payload.lastName].filter(Boolean).join(' ').trim();
  const studentName = pickFirstConcreteDisplayName(
    ['student'],
    payload.studentName,
    payload.userName,
    payload.username,
    legacyFullName,
    payload.name,
    payload.user,
    payload.registeredUser,
    payload.student,
    payload.studentData,
    payload.teacher,
    payload.teacherData
  );

  if (isPlaceholderValue(studentName, ['student'])) return null;

  return {
    titleKey: 'notifications.events.adminAlertUserRegistration.title',
    bodyKey: 'notifications.events.adminAlertUserRegistration.body',
    params: {
      studentName,
    },
  };
}

/**
 * Pick a localized string from an object map like `{ en: '...', pl: '...' }`.
 *
 * @param {unknown} map
 * @param {unknown} language
 * @returns {string}
 */
function pickFromLanguageMap(map, language) {
  if (!map) return '';
  if (typeof map === 'string') return map;
  if (typeof map !== 'object') return '';

  const normalized = normalizeLanguage(language);
  const short = normalized.split('-')[0];
  const exact = map[normalized];
  const shortMatch = map[short];
  const english = map.en;
  const polish = map.pl;
  const firstText = Object.values(map).find((value) => typeof value === 'string');

  const resolved = exact || shortMatch || english || polish || firstText;
  return typeof resolved === 'string' ? resolved : '';
}

/**
 * Interpolate `{{token}}` placeholders from params.
 *
 * @param {unknown} template
 * @param {Record<string, any>} params
 * @returns {string}
 */
function interpolateTemplate(template, params = {}) {
  const text = String(template || '');
  if (!text) return '';
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, token) => {
    const value = params[token];
    return value == null ? '' : String(value);
  });
}

const LESSON_BOOKED_MESSAGE_RE =
  /^(Trial booking|New booking) created for (\d{4}-\d{2}-\d{2}) from (\d{2}:\d{2}) to (\d{2}:\d{2})\.\s*(?:Student:\s*(.+))?\s*$/;
const LESSON_BOOKED_MESSAGE_RE_PL =
  /^Utworzono\s+now(?:ą|a|Ä…)\s+rezerwacj(?:ę|e|Ä™)\s+dla\s+(\d{2}\.\d{2}\.\d{4})\s+od\s+(\d{2}:\d{2})\s+do\s+(\d{2}:\d{2})\.\s*(?:(?:Uczeń|Uczen|UczeĹ„):\s*(.+?))?\s*$/i;
const LESSON_BOOKED_MESSAGE_RE_PL_TRIAL =
  /^Rezerwacja\s+(?:próbna|probna|prĂłbna)\s+utworzona\s+dla\s+(\d{2}\.\d{2}\.\d{4})\s+od\s+(\d{2}:\d{2})\s+do\s+(\d{2}:\d{2})\.\s*(?:(?:Uczeń|Uczen|UczeĹ„):\s*(.+?))?\s*$/i;

const LESSON_BOOKED_STUDENT_MESSAGE_RE_EN =
  /^(?:New\s+)?lesson booked with (.+) for (\d{4}-\d{2}-\d{2}) from (\d{2}:\d{2}) to (\d{2}:\d{2})\.\s*$/i;

const LESSON_BOOKED_STUDENT_MESSAGE_RE_PL =
  /^Zarezerwowano\s+lekcj(?:ę|e|Ä™)\s+z\s+(.+?)\s+na\s+(\d{2}\.\d{2}\.\d{4})\s+w\s+godz\.?\s+(\d{2}:\d{2})\s*[-â€“]\s*(\d{2}:\d{2})\.\s*$/i;

/**
 * Legacy "lesson booked" notifications were stored as plain English strings.
 * Try to parse those and map them to translation keys.
 *
 * @param {unknown} text
 * @returns {{ titleKey: string, bodyKey: string, params: Record<string, any> } | null}
 */
function sanitizeTeacherNameForDisplay(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const withoutPrefix = raw.replace(/^(teacher|nauczyciel)\s*[:-]?\s+/i, '').trim();
  const candidate = withoutPrefix || raw;
  return candidate.split(/\s+/)[0] || '';
}

function plDateToIso(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return raw;
  const [, d, m, y] = match;
  return `${y}-${m}-${d}`;
}

/**
 * Legacy "lesson booked (student)" notifications were stored as plain strings.
 *
 * We parse both EN and PL variants so older notifications can render via i18n keys and match
 * the current UI language (instead of being stuck in whatever language was stored).
 *
 * @param {unknown} text
 * @returns {{ titleKey: string, bodyKey: string, params: Record<string, any> } | null}
 */
export function deriveLessonBookedStudentI18nFromText(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const en = raw.match(LESSON_BOOKED_STUDENT_MESSAGE_RE_EN);
  if (en) {
    const teacher = sanitizeTeacherNameForDisplay(en[1]);
    const date = en[2];
    const start = en[3];
    const end = en[4];
    return {
      titleKey: 'notifications.events.lessonBookedStudent.title',
      bodyKey: 'notifications.events.lessonBookedStudent.body',
      params: { teacher, date, start, end },
    };
  }

  const pl = raw.match(LESSON_BOOKED_STUDENT_MESSAGE_RE_PL);
  if (pl) {
    const teacher = sanitizeTeacherNameForDisplay(pl[1]);
    const date = plDateToIso(pl[2]);
    const start = pl[3];
    const end = pl[4];
    return {
      titleKey: 'notifications.events.lessonBookedStudent.title',
      bodyKey: 'notifications.events.lessonBookedStudent.body',
      params: { teacher, date, start, end },
    };
  }

  return null;
}

/**
 * Legacy "lesson booked" notifications were stored as plain strings.
 *
 * This helper keeps backward compatibility for both regular and trial lesson bookings so the
 * notification center can render old feed items using the proper localized template keys.
 *
 * @param {unknown} text
 * @returns {{ titleKey: string, bodyKey: string, params: Record<string, any> } | null}
 */
export function deriveLessonBookedI18nFromText(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const match = raw.match(LESSON_BOOKED_MESSAGE_RE);
  if (match) {
    const bookingType = String(match[1] || '').toLowerCase();
    const date = match[2];
    const start = match[3];
    const end = match[4];
    const student = String(match[5] || '').trim();
    const isTrial = bookingType.includes('trial');

    return {
      titleKey: isTrial
        ? 'notifications.events.lessonBooked.trialTitle'
        : 'notifications.events.lessonBooked.title',
      bodyKey: isTrial
        ? (student ? 'notifications.events.lessonBooked.trialBodyWithStudent' : 'notifications.events.lessonBooked.trialBody')
        : (student ? 'notifications.events.lessonBooked.bodyWithStudent' : 'notifications.events.lessonBooked.body'),
      params: {
        date,
        start,
        end,
        student,
      },
    };
  }

  const plRegular = raw.match(LESSON_BOOKED_MESSAGE_RE_PL);
  if (plRegular) {
    const date = plDateToIso(plRegular[1]);
    const start = plRegular[2];
    const end = plRegular[3];
    const student = String(plRegular[4] || '').trim().replace(/\.$/, '');

    return {
      titleKey: 'notifications.events.lessonBooked.title',
      bodyKey: student
        ? 'notifications.events.lessonBooked.bodyWithStudent'
        : 'notifications.events.lessonBooked.body',
      params: {
        date,
        start,
        end,
        student,
      },
    };
  }

  const plTrial = raw.match(LESSON_BOOKED_MESSAGE_RE_PL_TRIAL);
  if (plTrial) {
    const date = plDateToIso(plTrial[1]);
    const start = plTrial[2];
    const end = plTrial[3];
    const student = String(plTrial[4] || '').trim().replace(/\.$/, '');

    return {
      titleKey: 'notifications.events.lessonBooked.trialTitle',
      bodyKey: student
        ? 'notifications.events.lessonBooked.trialBodyWithStudent'
        : 'notifications.events.lessonBooked.trialBody',
      params: {
        date,
        start,
        end,
        student,
      },
    };
  }

  return null;
}

/**
 * Derive a "payment completed" i18n mapping from structured payload fields.
 *
 * This is both a fallback for legacy notifications (where the backend stored an English string),
 * and a safety net if a producer forgets to include `payload.i18n`.
 *
 * @param {any} payload
 * @returns {{ titleKey: string, bodyKey: string, params: Record<string, any> } | null}
 */
export function derivePaymentCompletedI18nFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (isAdminPaymentAlertPayload(payload)) return null;

  const category = String(payload.category || '').toLowerCase();
  if (category !== 'payment') return null;

  const hours = payload.hours ?? payload.sessions;
  const packageName = payload.packageName;
  if (hours != null && packageName) {
    return {
      titleKey: 'notifications.events.paymentCompleted.title',
      bodyKey: 'notifications.events.paymentCompleted.body',
      params: { hours, packageName },
    };
  }

  const amount =
    payload.amount ??
    payload.purchaseAmount ??
    payload.Purchase_Amount ??
    payload.totalAmount ??
    payload.value;
  const currency = payload.currency ?? payload.currencyCode;
  if (amount == null || amount === '' || !currency) return null;

  return {
    titleKey: 'notifications.events.paymentCompleted.title',
    bodyKey: 'notifications.events.paymentCompleted.bodyAmount',
    params: {
      amount: String(amount),
      currency: String(currency).toUpperCase(),
    },
  };
}

const PAYMENT_COMPLETED_MESSAGE_RE =
  /^Your payment has been processed successfully\.\s*([\d.,]+)\s*hours?\s*have been added to your wallet for\s*(.+)\.\s*$/i;
const PAYMENT_COMPLETED_AMOUNT_MESSAGE_RE =
  /^Your payment of\s*([\d.,]+)\s*([A-Za-z]{3})\s*was completed successfully\.\s*$/i;

/**
 * Last-resort parsing of legacy payment text when structured payload fields are missing.
 *
 * @param {unknown} text
 * @returns {{ titleKey: string, bodyKey: string, params: Record<string, any> } | null}
 */
export function derivePaymentCompletedI18nFromText(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const match = raw.match(PAYMENT_COMPLETED_MESSAGE_RE);
  if (match) {
    const hours = match[1];
    const packageName = String(match[2] || '').trim();
    if (!packageName) return null;

    return {
      titleKey: 'notifications.events.paymentCompleted.title',
      bodyKey: 'notifications.events.paymentCompleted.body',
      params: { hours, packageName },
    };
  }

  const amountMatch = raw.match(PAYMENT_COMPLETED_AMOUNT_MESSAGE_RE);
  if (!amountMatch) return null;

  const amount = String(amountMatch[1] || '').trim();
  const currency = String(amountMatch[2] || '').trim().toUpperCase();
  if (!amount || !currency) return null;

  return {
    titleKey: 'notifications.events.paymentCompleted.title',
    bodyKey: 'notifications.events.paymentCompleted.bodyAmount',
    params: { amount, currency },
  };
}

const LEGACY_LESSON_CANCELLED_RE =
  /^Your lesson with (.+) on (\d{4}-\d{2}-\d{2}) at (\d{2}:\d{2}\s*-\s*\d{2}:\d{2}) has been canceled\.\s*$/i;
const LEGACY_LESSON_START_NOW_RE =
  /^Your lesson with (.+) is starting now\. Join immediately\.\s*$/i;
const LEGACY_LESSON_REMINDER_10M_RE =
  /^Starting soon:\s*Your lesson with (.+) begins in about 10 minutes at (.+?)\.\s*$/i;
const LEGACY_LESSON_REMINDER_1H_RE =
  /^Starting soon:\s*Your lesson with (.+) begins at (.+?)\.\s*$/i;
const LEGACY_LESSON_REMINDER_24H_RE =
  /^Lesson reminder:\s*(.+?)\s*(?:\u2014|\u2013|-)\s*(\d{4}-\d{2}-\d{2})\s+(.+?)\.\s*$/i;
const LEGACY_STUDENT_WAITING_RE =
  /^Student\s+(.+?)\s+is waiting in the lesson room\. Join now\.\s*$/i;
const LEGACY_TEACHER_WAITING_RE =
  /^(.+?)\s+is waiting in the classroom\. Join the lesson now\.\s*$/i;
const LEGACY_LESSON_NO_SHOW_RE =
  /^You did not attend your lesson with (.+?) on (\d{4}-\d{2}-\d{2}) at (.+?)\.\s*$/i;

const LEGACY_LESSON_REMINDER_24H_RE_FALLBACK =
  /^Lesson reminder:\s*(.+?)\s*(?:\u2014|\u2013|-|â€”|â€“|Ă˘â‚¬â€ť|Ă˘â‚¬â€ś)\s*(\d{4}-\d{2}-\d{2})\s+(.+?)\.\s*$/i;

function matchLegacyLessonReminder24h(raw) {
  return raw.match(LEGACY_LESSON_REMINDER_24H_RE) || raw.match(LEGACY_LESSON_REMINDER_24H_RE_FALLBACK);
}

export function deriveLegacyLessonLifecycleI18nFromText(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const cancelled = raw.match(LEGACY_LESSON_CANCELLED_RE);
  if (cancelled) {
    return {
      titleKey: 'notifications.events.lessonCancelled.title',
      bodyKey: 'notifications.events.lessonCancelled.body',
      params: {
        teacher: cancelled[1],
        date: cancelled[2],
        time: cancelled[3],
      },
    };
  }

  const startNow = raw.match(LEGACY_LESSON_START_NOW_RE);
  if (startNow) {
    return {
      titleKey: 'notifications.events.lessonStartNow.title',
      bodyKey: 'notifications.events.lessonStartNow.body',
      params: {
        counterpart: startNow[1],
      },
    };
  }

  const reminder10m = raw.match(LEGACY_LESSON_REMINDER_10M_RE);
  if (reminder10m) {
    return {
      titleKey: 'notifications.events.lessonReminder10m.title',
      bodyKey: 'notifications.events.lessonReminder10m.body',
      params: {
        counterpart: reminder10m[1],
        time: reminder10m[2],
      },
    };
  }

  const reminder1h = raw.match(LEGACY_LESSON_REMINDER_1H_RE);
  if (reminder1h) {
    return {
      titleKey: 'notifications.events.lessonReminder1h.title',
      bodyKey: 'notifications.events.lessonReminder1h.body',
      params: {
        counterpart: reminder1h[1],
        time: reminder1h[2],
      },
    };
  }

  const reminder24h = matchLegacyLessonReminder24h(raw);
  if (reminder24h) {
    return {
      titleKey: 'notifications.events.lessonReminder24h.title',
      bodyKey: 'notifications.events.lessonReminder24h.body',
      params: {
        counterpart: reminder24h[1],
        date: reminder24h[2],
        time: reminder24h[3],
      },
    };
  }

  const studentWaiting = raw.match(LEGACY_STUDENT_WAITING_RE);
  if (studentWaiting) {
    return {
      titleKey: 'notifications.events.lessonStudentWaiting.title',
      bodyKey: 'notifications.events.lessonStudentWaiting.body',
      params: {
        student: studentWaiting[1],
      },
    };
  }

  const teacherWaiting = raw.match(LEGACY_TEACHER_WAITING_RE);
  if (teacherWaiting) {
    return {
      titleKey: 'notifications.events.lessonTeacherWaiting.title',
      bodyKey: 'notifications.events.lessonTeacherWaiting.body',
      params: {
        teacher: teacherWaiting[1],
      },
    };
  }

  const noShow = raw.match(LEGACY_LESSON_NO_SHOW_RE);
  if (noShow) {
    return {
      titleKey: 'notifications.events.lessonNoShowStudent.title',
      bodyKey: 'notifications.events.lessonNoShowStudent.body',
      params: {
        counterpart: noShow[1],
        date: noShow[2],
        time: noShow[3],
      },
    };
  }

  return null;
}

/**
 * Event-type-based i18n key resolver.
 *
 * Maps internal event types (from payload.eventType) to i18n title/body keys.
 * This acts as a safety net when payload.i18n metadata is missing or was stripped
 * during the Novu pipeline (e.g. nested objects not preserved).
 *
 * @param {any} payload
 * @returns {{ titleKey: string, bodyKey: string, params: Record<string, any> } | null}
 */
const EVENT_TYPE_I18N_MAP = {
  LESSON_NO_SHOW_TEACHER: {
    titleKey: 'notifications.events.lessonNoShowTeacher.title',
    bodyKey: 'notifications.events.lessonNoShowTeacher.body',
  },
  LESSON_NO_SHOW_STUDENT: {
    titleKey: 'notifications.events.lessonNoShowStudent.title',
    bodyKey: 'notifications.events.lessonNoShowStudent.body',
  },
  LESSON_COMPLETED: {
    titleKey: 'notifications.events.lessonCompleted.title',
    bodyKey: 'notifications.events.lessonCompleted.body',
  },
  LESSON_CANCELED: {
    titleKey: 'notifications.events.lessonCancelled.title',
    bodyKey: 'notifications.events.lessonCancelled.body',
  },
  LESSON_RESCHEDULED: {
    titleKey: 'notifications.events.lessonRescheduled.title',
    bodyKey: 'notifications.events.lessonRescheduled.body',
  },
  LESSON_START_NOW: {
    titleKey: 'notifications.events.lessonStartNow.title',
    bodyKey: 'notifications.events.lessonStartNow.body',
  },
  LESSON_TEACHER_WAITING: {
    titleKey: 'notifications.events.lessonTeacherWaiting.title',
    bodyKey: 'notifications.events.lessonTeacherWaiting.body',
  },
  LESSON_STUDENT_WAITING: {
    titleKey: 'notifications.events.lessonStudentWaiting.title',
    bodyKey: 'notifications.events.lessonStudentWaiting.body',
  },
  LESSON_REMINDER_24H: {
    titleKey: 'notifications.events.lessonReminder24h.title',
    bodyKey: 'notifications.events.lessonReminder24h.body',
  },
  LESSON_REMINDER_2H: {
    titleKey: 'notifications.events.lessonReminder2h.title',
    bodyKey: 'notifications.events.lessonReminder2h.body',
  },
  LESSON_REMINDER_1H: {
    titleKey: 'notifications.events.lessonReminder1h.title',
    bodyKey: 'notifications.events.lessonReminder1h.body',
  },
  LESSON_REMINDER_10M: {
    titleKey: 'notifications.events.lessonReminder10m.title',
    bodyKey: 'notifications.events.lessonReminder10m.body',
  },
};

export function deriveI18nFromEventType(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const eventType = String(payload.eventType || '').trim();
  if (!eventType) return null;

  const mapping = EVENT_TYPE_I18N_MAP[eventType];
  if (!mapping) return null;

  return {
    titleKey: mapping.titleKey,
    bodyKey: mapping.bodyKey,
    params: {},
  };
}

/**
 * Novu notifications can expose workflow payload fields under different property names.
 * We normalize to a single "payload" object to simplify consumers.
 *
 * @param {any} notification
 * @returns {Record<string, any>}
 */
export function extractNotificationPayload(notification) {
  const data = notification?.data && typeof notification.data === 'object' ? notification.data : null;
  const payload = notification?.payload && typeof notification.payload === 'object' ? notification.payload : null;
  const nestedPayload = data?.payload && typeof data.payload === 'object' ? data.payload : null;

  if (data) {
    return {
      ...data,
      ...(nestedPayload || {}),
    };
  }

  return payload || {};
}

/**
 * Best-effort, language-aware title/body extraction for a Novu notification object.
 *
 * @param {any} notification
 * @param {(key: string, opts?: any) => string} t
 * @param {string} language
 * @returns {{ title: string, body: string }}
 */
export function getNotificationText(notification, t, language) {
  const payload = extractNotificationPayload(notification);

  const canonicalTitle = typeof notification?.Notification_title === 'string'
    ? notification.Notification_title
    : typeof notification?.title === 'string'
      ? notification.title
      : null;
  const canonicalBody = typeof notification?.Notification_body === 'string'
    ? notification.Notification_body
    : typeof notification?.body === 'string'
      ? notification.body
      : null;

  const rawTitle =
    canonicalTitle !== null
      ? canonicalTitle
      : payload?.title ||
        notification?.subject ||
        payload?.message ||
        (typeof notification?.content === 'string' ? notification.content : null) ||
        t('notifications.toast.title', { defaultValue: 'New notification' });

  const rawTitleString = canonicalTitle === ''
    ? ''
    : String(rawTitle || t('notifications.toast.title', { defaultValue: 'New notification' }));

  const rawBody =
    canonicalTitle !== null && canonicalBody !== null
      ? canonicalBody
      : payload?.message ||
        notification?.body ||
        (typeof notification?.content === 'string' && notification.content !== rawTitleString ? notification.content : null) ||
        payload?.smsMessage ||
        payload?.whatsappMessage ||
        '';

  const initialRawBodyString = rawBody == null ? '' : String(rawBody);
  const rawBodyString = shouldSuppressPlaceholderAdminBody(payload, rawTitleString, initialRawBodyString)
    ? ''
    : initialRawBodyString;

  const explicitI18nMeta = hasIncompletePlaceholderAdminI18n(payload, rawTitleString, payload?.i18n)
    ? null
    : payload?.i18n;

  const i18nMeta =
    explicitI18nMeta ||
    deriveI18nFromEventType(payload) ||
    deriveLegacyLessonLifecycleI18nFromText(rawBodyString) ||
    deriveLegacyLessonLifecycleI18nFromText(rawTitleString) ||
    deriveAdminAlertBookingI18nFromPayload(payload, rawTitleString) ||
    deriveAdminAlertPaymentI18nFromPayload(payload, rawTitleString) ||
    deriveAdminAlertUserRegistrationI18nFromPayload(payload, rawTitleString) ||
    derivePaymentCompletedI18nFromPayload(payload) ||
    derivePaymentCompletedI18nFromText(rawBodyString) ||
    deriveLessonBookedStudentI18nFromText(rawBodyString) ||
    deriveLessonBookedStudentI18nFromText(rawTitleString) ||
    deriveLessonBookedI18nFromText(rawBodyString) ||
    deriveLessonBookedI18nFromText(rawTitleString);

  const fallbackParams = deriveI18nParamsFromPayload(payload, language);
  const params = {
    ...fallbackParams,
    ...(i18nMeta?.params && typeof i18nMeta.params === 'object' ? i18nMeta.params : {}),
  };

  // Apply iso-date formatting for any param that looks like a date key.
  for (const key of Object.keys(params)) {
    if (!key) continue;
    if (key === 'date' || key.toLowerCase().includes('date')) {
      params[key] = formatIsoDateForLanguage(params[key], language);
    }
  }

  const mappedTitle = interpolateTemplate(pickFromLanguageMap(i18nMeta?.titleMap, language), params);
  const mappedBody = interpolateTemplate(pickFromLanguageMap(i18nMeta?.bodyMap, language), params);

  const title = canonicalTitle !== null
    ? canonicalTitle
    : mappedTitle || (i18nMeta?.titleKey
      ? String(t(i18nMeta.titleKey, { ...params, defaultValue: rawTitleString }))
      : rawTitleString);

  const body = canonicalTitle !== null && canonicalBody !== null
    ? canonicalBody
    : mappedBody || (i18nMeta?.bodyKey
      ? String(t(i18nMeta.bodyKey, { ...params, defaultValue: rawBodyString }))
      : rawBodyString);

  return { title, body: title === body ? '' : body };
}
